import { useState } from "react";
import toast from "react-hot-toast";
import { Link, useNavigate } from "react-router-dom";
import {
    useChangePassword,
    useLogout,
    useUpdateProfile,
    useUser,
} from "../../hooks";
import { applyPhoneMask } from "../../utils";

type TabType = "info" | "settings";

interface EditFormData {
  firstName: string;
  lastName: string;
  phone: string;
}

interface PasswordFormData {
  oldPassword: string;
  newPassword: string;
  confirmPassword: string;
}

/**
 * Get display name (first two names from full name)
 */
function getDisplayName(fullName?: string): string {
  if (!fullName) return "User";
  const names = fullName.trim().split(/\s+/);
  if (names.length <= 2) return fullName;
  return `${names[0]} ${names[1]}`;
}

/**
 * Format date to readable string
 */
function formatJoinDate(dateString?: string): string {
  if (!dateString) return "Recently joined";
  const date = new Date(dateString);
  return `Since ${date.toLocaleDateString("en-US", { month: "short", year: "numeric" })}`;
}

export function ProfilePage() {
  const { data: user, isLoading } = useUser();
  const navigate = useNavigate();
  const logout = useLogout();
  const updateProfileMutation = useUpdateProfile();
  const changePasswordMutation = useChangePassword();

  const handleLogout = () => {
    logout();
    navigate("/login");
  };

  const [activeTab, setActiveTab] = useState<TabType>("info");
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);
  const [showOldPassword, setShowOldPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const [editForm, setEditForm] = useState<EditFormData>({
    firstName: user?.firstName || "",
    lastName: user?.lastName || "",
    phone: user?.phone || "",
  });

  const [passwordForm, setPasswordForm] = useState<PasswordFormData>({
    oldPassword: "",
    newPassword: "",
    confirmPassword: "",
  });

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    updateProfileMutation.mutate(editForm, {
      onSuccess: () => {
        setIsEditModalOpen(false);
      },
      onError: () => {
        toast.error("Failed to update profile. Please try again.");
      },
    });
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      toast.error("New passwords do not match");
      return;
    }

    if (passwordForm.newPassword.length < 8) {
      toast.error("Password must be at least 8 characters");
      return;
    }

    changePasswordMutation.mutate(
      {
        oldPassword: passwordForm.oldPassword,
        newPassword: passwordForm.newPassword,
        newPasswordConfirm: passwordForm.confirmPassword,
      },
      {
        onSuccess: () => {
          setIsPasswordModalOpen(false);
          setPasswordForm({
            oldPassword: "",
            newPassword: "",
            confirmPassword: "",
          });
          setShowOldPassword(false);
          setShowNewPassword(false);
          setShowConfirmPassword(false);
        },
      },
    );
  };

  const openEditModal = () => {
    // Reset form with current user data
    setEditForm({
      firstName: user?.firstName || "",
      lastName: user?.lastName || "",
      phone: user?.phone || "",
    });
    setIsEditModalOpen(true);
  };

  const openPasswordModal = () => {
    setPasswordForm({
      oldPassword: "",
      newPassword: "",
      confirmPassword: "",
    });
    setShowOldPassword(false);
    setShowNewPassword(false);
    setShowConfirmPassword(false);
    setIsPasswordModalOpen(true);
  };

  const displayName = getDisplayName(user?.fullName);

  const whatsappNumber = "558197160139";
  const whatsappMessage = encodeURIComponent(
    "Hi! I have a question about my classes.",
  );

  // Show loading state while fetching user data
  if (isLoading) {
    return (
      <div className="min-h-screen bg-sand-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-2 border-earth-300 border-t-earth-600 rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-earth-600">Loading profile...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-sand-50">
      <div className="max-w-3xl mx-auto px-4 sm:px-6 py-4 sm:py-10">
        {/* Header */}
        <div className="mb-5 sm:mb-8">
          <Link
            to="/"
            className="inline-flex items-center gap-1 text-earth-700 hover:text-earth-700 text-sm mb-2 transition-colors"
          >
            <i className="ri-arrow-left-line"></i>
            Back
          </Link>
          <h1 className="text-2xl sm:text-3xl font-display font-bold text-earth-800">
            My Profile
          </h1>
        </div>

        {/* Profile Card */}
        <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden mb-4 sm:mb-6">
          <div className="p-4 sm:p-6 flex flex-col sm:flex-row items-center sm:items-start gap-4 sm:gap-5">
            {/* Avatar */}
            <div className="relative">
              <div className="w-16 h-16 sm:w-20 sm:h-20 rounded-2xl bg-gradient-to-br from-olive-500 to-olive-600 flex items-center justify-center text-white text-2xl sm:text-3xl font-display font-bold">
                {user?.fullName?.charAt(0)?.toUpperCase() || "A"}
              </div>
            </div>

            {/* Info */}
            <div className="flex-1 text-center sm:text-left min-w-0">
              <h2 className="text-lg sm:text-xl font-display font-bold text-earth-800 mb-0.5 sm:mb-1 truncate">
                {displayName}
              </h2>
              <p className="text-earth-700 text-sm mb-2 sm:mb-3 truncate">
                {user?.email || ""}
              </p>
              <div className="flex flex-wrap items-center justify-center sm:justify-start gap-1.5 sm:gap-2">
                {user?.currentLevel && (
                  <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-lg bg-olive-100 text-olive-700 text-xs sm:text-sm font-medium">
                    <i className="ri-graduation-cap-line"></i>
                    Level {user.currentLevel}
                  </span>
                )}
                <span className="inline-flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1 rounded-lg bg-earth-100 text-earth-600 text-xs sm:text-sm">
                  <i className="ri-calendar-line"></i>
                  {formatJoinDate(user?.dateJoined)}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* Tabs */}
        <div className="flex gap-1 p-1 bg-white border border-earth-200 rounded-xl mb-4 sm:mb-6">
          <button
            onClick={() => setActiveTab("info")}
            className={`flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activeTab === "info"
                ? "bg-earth-800 text-white"
                : "text-earth-600 hover:bg-earth-50 active:bg-earth-100"
            }`}
          >
            <i className="ri-user-line mr-1 sm:mr-1.5"></i>
            Info
          </button>
          <button
            onClick={() => setActiveTab("settings")}
            className={`flex-1 px-3 sm:px-4 py-2 sm:py-2.5 rounded-lg text-xs sm:text-sm font-medium transition-colors ${
              activeTab === "settings"
                ? "bg-earth-800 text-white"
                : "text-earth-600 hover:bg-earth-50 active:bg-earth-100"
            }`}
          >
            <i className="ri-settings-3-line mr-1 sm:mr-1.5"></i>
            Settings
          </button>
        </div>

        {/* Content */}
        {activeTab === "info" && (
          <div className="space-y-4">
            {/* Personal Info */}
            <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden">
              <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-earth-100 flex items-center justify-between">
                <h3 className="font-semibold text-earth-800 text-sm sm:text-base">
                  Personal data
                </h3>
                <button
                  onClick={openEditModal}
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-2.5 sm:px-3 py-1.5 bg-olive-50 hover:bg-olive-100 active:bg-olive-200 text-olive-700 rounded-lg text-xs sm:text-sm font-medium transition-colors"
                >
                  <i className="ri-edit-line"></i>
                  <span className="hidden xs:inline">Edit</span>
                </button>
              </div>
              <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
                <div className="flex items-center justify-between py-1.5 sm:py-2">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-earth-700">
                      Full name
                    </p>
                    <p className="font-medium text-earth-800 text-sm sm:text-base truncate">
                      {user?.fullName || "Not provided"}
                    </p>
                  </div>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-earth-100 gap-2">
                  <div className="min-w-0 flex-1">
                    <p className="text-xs sm:text-sm text-earth-700">Email</p>
                    <p className="font-medium text-earth-800 text-sm sm:text-base truncate">
                      {user?.email || ""}
                    </p>
                  </div>
                  <span className="text-[10px] sm:text-xs text-earth-700 bg-earth-50 px-1.5 sm:px-2 py-0.5 sm:py-1 rounded shrink-0">
                    Fixed
                  </span>
                </div>
                <div className="flex items-center justify-between py-2 border-t border-earth-100">
                  <div className="min-w-0">
                    <p className="text-xs sm:text-sm text-earth-700">Phone</p>
                    <p className="font-medium text-earth-800 text-sm sm:text-base">
                      {user?.phone || "Not provided"}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Class Info */}
            {user?.classInfo && (
              <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden">
                <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-earth-100">
                  <h3 className="font-semibold text-earth-800 text-sm sm:text-base">
                    Class information
                  </h3>
                </div>
                <div className="p-4 sm:p-5 space-y-3 sm:space-y-4">
                  <div className="flex items-center justify-between py-2 gap-2">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-earth-700">
                        Class type
                      </p>
                      <p className="font-medium text-earth-800 text-sm sm:text-base capitalize">
                        {user.classInfo.classType}
                      </p>
                    </div>
                    <span className="px-2 sm:px-2.5 py-0.5 sm:py-1 bg-olive-100 text-olive-700 rounded-lg text-[10px] sm:text-xs font-semibold shrink-0">
                      {user.classInfo.levelCode}
                    </span>
                  </div>
                  <div className="flex items-center justify-between py-2 border-t border-earth-100">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-earth-700">
                        Schedule
                      </p>
                      <p className="font-medium text-earth-800 text-sm sm:text-base">
                        {user.classInfo.schedule.days.join(", ")} at{" "}
                        {user.classInfo.schedule.time}
                      </p>
                    </div>
                  </div>
                  {user.classInfo.teacherName && (
                    <div className="flex items-center justify-between py-2 border-t border-earth-100">
                      <div className="min-w-0">
                        <p className="text-xs sm:text-sm text-earth-700">
                          Teacher
                        </p>
                        <p className="font-medium text-earth-800 text-sm sm:text-base">
                          {user.classInfo.teacherName}
                        </p>
                      </div>
                    </div>
                  )}
                  <div className="flex items-center justify-between py-2 border-t border-earth-100 gap-2">
                    <div className="min-w-0">
                      <p className="text-xs sm:text-sm text-earth-700">
                        Class group
                      </p>
                      <p className="font-medium text-earth-800 text-sm sm:text-base truncate">
                        {user.classInfo.classGroupName}
                      </p>
                    </div>
                    <span className="text-[10px] sm:text-xs text-earth-700 shrink-0">
                      {user.classInfo.studentCount}/{user.classInfo.maxStudents}{" "}
                      students
                    </span>
                  </div>
                  {user.classInfo.meetLink && (
                    <a
                      href={user.classInfo.meetLink}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-center justify-center gap-2 w-full mt-2 px-4 py-2.5 sm:py-3 bg-olive-600 hover:bg-olive-700 active:bg-olive-800 text-white rounded-xl text-xs sm:text-sm font-medium transition-colors"
                    >
                      <i className="ri-video-on-line"></i>
                      Open Google Meet
                    </a>
                  )}
                </div>
              </div>
            )}

            {/* Contact Support */}
            <a
              href={`https://wa.me/${whatsappNumber}?text=${whatsappMessage}`}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl border border-green-200 hover:border-green-300 active:border-green-400 transition-all group"
            >
              <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-xl bg-green-500 flex items-center justify-center shrink-0">
                <i className="ri-whatsapp-line text-white text-lg sm:text-xl"></i>
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-semibold text-earth-800 text-sm sm:text-base">
                  Need help?
                </p>
                <p className="text-xs sm:text-sm text-earth-700">
                  Chat on WhatsApp
                </p>
              </div>
              <i className="ri-arrow-right-line text-green-500 group-hover:translate-x-1 transition-transform"></i>
            </a>
          </div>
        )}

        {activeTab === "settings" && (
          <div className="space-y-4">
            {/* Account Actions */}
            <div className="bg-white rounded-2xl border border-earth-200 overflow-hidden">
              <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-earth-100">
                <h3 className="font-semibold text-earth-800 text-sm sm:text-base">
                  Account
                </h3>
              </div>
              <div className="divide-y divide-earth-100">
                <button
                  onClick={openPasswordModal}
                  className="w-full flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 hover:bg-earth-50 active:bg-earth-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <i className="ri-lock-password-line text-earth-500"></i>
                    <span className="font-medium text-earth-800 text-sm sm:text-base">
                      Change password
                    </span>
                  </div>
                  <i className="ri-arrow-right-s-line text-earth-400"></i>
                </button>
                <button
                  onClick={handleLogout}
                  className="w-full flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 hover:bg-red-50 active:bg-red-100 transition-colors text-left"
                >
                  <div className="flex items-center gap-3">
                    <i className="ri-logout-box-line text-red-500"></i>
                    <span className="font-medium text-red-600 text-sm sm:text-base">
                      Logout
                    </span>
                  </div>
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Edit Profile Modal */}
      {isEditModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-earth-900/50 backdrop-blur-sm"
            onClick={() => setIsEditModalOpen(false)}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-earth-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-olive-100 flex items-center justify-center">
                  <i className="ri-edit-line text-olive-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="font-display font-semibold text-earth-800 text-sm sm:text-base">
                    Edit Profile
                  </h3>
                  <p className="text-[10px] sm:text-xs text-earth-700">
                    Update your information
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsEditModalOpen(false)}
                className="p-2 hover:bg-earth-100 active:bg-earth-200 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-lg sm:text-xl text-earth-400"></i>
              </button>
            </div>

            {/* Form */}
            <form onSubmit={handleEditSubmit} className="flex-1 overflow-auto">
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {/* First Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-earth-700 mb-1 sm:mb-1.5">
                    First Name
                  </label>
                  <input
                    type="text"
                    value={editForm.firstName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, firstName: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Last Name */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-earth-700 mb-1 sm:mb-1.5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    value={editForm.lastName}
                    onChange={(e) =>
                      setEditForm({ ...editForm, lastName: e.target.value })
                    }
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                    required
                  />
                </div>

                {/* Phone */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-earth-700 mb-1 sm:mb-1.5">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={editForm.phone}
                    onChange={(e) =>
                      setEditForm({ ...editForm, phone: applyPhoneMask(e.target.value) })
                    }
                    maxLength={15}
                    placeholder="(11) 98765-4321"
                    className="w-full px-3 sm:px-4 py-2 sm:py-2.5 text-sm border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                  />
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-earth-100 flex items-center justify-end gap-2 sm:gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsEditModalOpen(false)}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 border border-earth-200 hover:bg-earth-50 active:bg-earth-100 text-earth-700 rounded-xl text-xs sm:text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateProfileMutation.isPending}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 bg-olive-600 hover:bg-olive-700 active:bg-olive-800 text-white rounded-xl text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2"
                >
                  {updateProfileMutation.isPending ? (
                    <>
                      <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <i className="ri-save-line"></i>
                      Save
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Change Password Modal */}
      {isPasswordModalOpen && (
        <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center sm:p-4">
          {/* Backdrop */}
          <div
            className="absolute inset-0 bg-earth-900/50 backdrop-blur-sm"
            onClick={() => setIsPasswordModalOpen(false)}
          />

          {/* Modal Content */}
          <div className="relative bg-white rounded-t-2xl sm:rounded-2xl shadow-xl w-full sm:max-w-lg max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-4 sm:px-6 py-3 sm:py-4 border-b border-earth-100 flex items-center justify-between shrink-0">
              <div className="flex items-center gap-2.5 sm:gap-3">
                <div className="w-9 h-9 sm:w-10 sm:h-10 rounded-xl bg-amber-100 flex items-center justify-center">
                  <i className="ri-lock-password-line text-amber-600 text-sm sm:text-base"></i>
                </div>
                <div>
                  <h3 className="font-display font-semibold text-earth-800 text-sm sm:text-base">
                    Change Password
                  </h3>
                  <p className="text-[10px] sm:text-xs text-earth-700">
                    Enter current and new password
                  </p>
                </div>
              </div>
              <button
                onClick={() => setIsPasswordModalOpen(false)}
                className="p-2 hover:bg-earth-100 active:bg-earth-200 rounded-lg transition-colors"
              >
                <i className="ri-close-line text-lg sm:text-xl text-earth-400"></i>
              </button>
            </div>

            {/* Form */}
            <form
              onSubmit={handlePasswordSubmit}
              className="flex-1 overflow-auto"
            >
              <div className="p-4 sm:p-6 space-y-3 sm:space-y-4">
                {/* Current Password */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-earth-700 mb-1 sm:mb-1.5">
                    Current Password
                  </label>
                  <div className="relative">
                    <input
                      type={showOldPassword ? "text" : "password"}
                      value={passwordForm.oldPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          oldPassword: e.target.value,
                        })
                      }
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 sm:pr-12 text-sm border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                      required
                    />
                    <button
                      type="button"
                      onClick={() => setShowOldPassword(!showOldPassword)}
                      className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 p-1 text-earth-400 hover:text-earth-600"
                    >
                      <i
                        className={
                          showOldPassword ? "ri-eye-off-line" : "ri-eye-line"
                        }
                      ></i>
                    </button>
                  </div>
                </div>

                {/* New Password */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-earth-700 mb-1 sm:mb-1.5">
                    New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showNewPassword ? "text" : "password"}
                      value={passwordForm.newPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          newPassword: e.target.value,
                        })
                      }
                      className="w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 sm:pr-12 text-sm border border-earth-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent"
                      required
                      minLength={8}
                    />
                    <button
                      type="button"
                      onClick={() => setShowNewPassword(!showNewPassword)}
                      className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 p-1 text-earth-400 hover:text-earth-600"
                    >
                      <i
                        className={
                          showNewPassword ? "ri-eye-off-line" : "ri-eye-line"
                        }
                      ></i>
                    </button>
                  </div>
                  <p className="text-[10px] sm:text-xs text-earth-700 mt-1">
                    Minimum 8 characters
                  </p>
                </div>

                {/* Confirm New Password */}
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-earth-700 mb-1 sm:mb-1.5">
                    Confirm New Password
                  </label>
                  <div className="relative">
                    <input
                      type={showConfirmPassword ? "text" : "password"}
                      value={passwordForm.confirmPassword}
                      onChange={(e) =>
                        setPasswordForm({
                          ...passwordForm,
                          confirmPassword: e.target.value,
                        })
                      }
                      className={`w-full px-3 sm:px-4 py-2 sm:py-2.5 pr-10 sm:pr-12 text-sm border rounded-xl focus:outline-none focus:ring-2 focus:ring-olive-500 focus:border-transparent ${
                        passwordForm.confirmPassword &&
                        passwordForm.newPassword !==
                          passwordForm.confirmPassword
                          ? "border-red-300 bg-red-50"
                          : "border-earth-200"
                      }`}
                      required
                    />
                    <button
                      type="button"
                      onClick={() =>
                        setShowConfirmPassword(!showConfirmPassword)
                      }
                      className="absolute right-2.5 sm:right-3 top-1/2 -translate-y-1/2 p-1 text-earth-400 hover:text-earth-600"
                    >
                      <i
                        className={
                          showConfirmPassword
                            ? "ri-eye-off-line"
                            : "ri-eye-line"
                        }
                      ></i>
                    </button>
                  </div>
                  {passwordForm.confirmPassword &&
                    passwordForm.newPassword !==
                      passwordForm.confirmPassword && (
                      <p className="text-[10px] sm:text-xs text-red-500 mt-1">
                        Passwords do not match
                      </p>
                    )}
                </div>
              </div>

              {/* Footer */}
              <div className="px-4 sm:px-6 py-3 sm:py-4 border-t border-earth-100 flex items-center justify-end gap-2 sm:gap-3 shrink-0">
                <button
                  type="button"
                  onClick={() => setIsPasswordModalOpen(false)}
                  className="px-3 sm:px-4 py-2 sm:py-2.5 border border-earth-200 hover:bg-earth-50 active:bg-earth-100 text-earth-700 rounded-xl text-xs sm:text-sm font-medium transition-colors"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={
                    changePasswordMutation.isPending ||
                    !passwordForm.oldPassword ||
                    !passwordForm.newPassword ||
                    passwordForm.newPassword !== passwordForm.confirmPassword
                  }
                  className="px-3 sm:px-4 py-2 sm:py-2.5 bg-amber-600 hover:bg-amber-700 active:bg-amber-800 text-white rounded-xl text-xs sm:text-sm font-medium transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1.5 sm:gap-2"
                >
                  {changePasswordMutation.isPending ? (
                    <>
                      <div className="w-3.5 h-3.5 sm:w-4 sm:h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                      Changing...
                    </>
                  ) : (
                    <>
                      <i className="ri-lock-line"></i>
                      Change
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
